// src/components/assessment/RiskFactorInput.jsx
import React, { useState } from 'react';
import {
    Grid, TextField, Typography, Divider, Box,
    FormControl, InputLabel, Select, MenuItem,
    Chip, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';

const RiskFactorInput = ({ domain, values, handleChange, setFieldValue, errors, touched }) => {
    // Add state to manage the dialog
    const [openVulnDialog, setOpenVulnDialog] = useState(false);
    const [newVuln, setNewVuln] = useState({ severity: 'low', count: 1 });

    const [openCreditDialog, setOpenCreditDialog] = useState(false);
    const [newCredit, setNewCredit] = useState({ type: 'credit_card', status: 'good', age_months: 12 });

    const [openHazardDialog, setOpenHazardDialog] = useState(false);
    const [newHazard, setNewHazard] = useState('');

    const [openDependencyDialog, setOpenDependencyDialog] = useState(false);
    const [newDependency, setNewDependency] = useState('');

    if (!domain) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body1" color="error">
                    Please select a risk domain first.
                </Typography>
            </Box>
        );
    }

    // Helper function to add items to arrays (for lists like vulnerabilities)
    const addListItem = (fieldName, itemStructure) => {
        // Get the current value, handling nested paths
        let currentValue;

        if (fieldName.includes('.')) {
            // Handle nested path like 'securityData.vulnerabilities'
            const [parent, child] = fieldName.split('.');
            currentValue = values[parent] && values[parent][child];
        } else {
            currentValue = values[fieldName];
        }

        // Make sure the current value is an array before spreading
        const currentItems = Array.isArray(currentValue)
            ? [...currentValue]
            : [];

        currentItems.push(itemStructure);

        // Update the form value
        setFieldValue(fieldName, currentItems);
    };

    // Helper function to remove items from arrays
    const removeListItem = (fieldName, index) => {
        // Get the current value, handling nested paths
        let currentValue;

        if (fieldName.includes('.')) {
            // Handle nested path like 'securityData.vulnerabilities'
            const [parent, child] = fieldName.split('.');
            currentValue = values[parent] && values[parent][child];
        } else {
            currentValue = values[fieldName];
        }

        // Ensure it's an array
        if (!Array.isArray(currentValue)) {
            console.warn(`Field ${fieldName} is not an array`);
            return;
        }

        // Create a copy of the array and remove the item
        const currentItems = [...currentValue];
        currentItems.splice(index, 1);

        // Update the form value
        setFieldValue(fieldName, currentItems);
    };

    // Render different form fields based on the selected domain
    switch (domain) {
        case 'financial':
            return (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6">Financial Risk Factors</Typography>
                        <Divider sx={{ my: 1 }} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            id="financialData.annualIncome"
                            name="financialData.annualIncome"
                            label="Annual Income ($)"
                            type="number"
                            value={values.financialData.annualIncome}
                            onChange={handleChange}
                            InputProps={{ inputProps: { min: 0 } }}
                            helperText="Enter your annual income before taxes"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            id="financialData.totalDebt"
                            name="financialData.totalDebt"
                            label="Total Debt ($)"
                            type="number"
                            value={values.financialData.totalDebt}
                            onChange={handleChange}
                            InputProps={{ inputProps: { min: 0 } }}
                            helperText="Sum of all outstanding debt"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Credit History
                        </Typography>

                        <Box sx={{ mb: 2 }}>
                            {values.financialData.creditHistory.map((item, index) => (
                                <Paper
                                    key={index}
                                    variant="outlined"
                                    sx={{ p: 2, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                >
                                    <Box>
                                        <Typography variant="body2">
                                            <strong>Type:</strong> {item.type}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Status:</strong> {item.status}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Age:</strong> {item.age_months} months
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        color="error"
                                        onClick={() => removeListItem('financialData.creditHistory', index)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Paper>
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => setOpenCreditDialog(true)}
                            >
                                Add Credit History Item
                            </Button>
                        </Box>

                        {/* Dialog for adding credit history */}
                        <Dialog open={openCreditDialog} onClose={() => setOpenCreditDialog(false)}>
                            <DialogTitle>Add Credit History Item</DialogTitle>
                            <DialogContent>
                                <FormControl fullWidth sx={{ mt: 2 }}>
                                    <InputLabel>Account Type</InputLabel>
                                    <Select
                                        value={newCredit.type}
                                        label="Account Type"
                                        onChange={(e) => setNewCredit({ ...newCredit, type: e.target.value })}
                                    >
                                        <MenuItem value="credit_card">Credit Card</MenuItem>
                                        <MenuItem value="mortgage">Mortgage</MenuItem>
                                        <MenuItem value="auto_loan">Auto Loan</MenuItem>
                                        <MenuItem value="personal_loan">Personal Loan</MenuItem>
                                        <MenuItem value="student_loan">Student Loan</MenuItem>
                                        <MenuItem value="line_of_credit">Line of Credit</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth sx={{ mt: 2 }}>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={newCredit.status}
                                        label="Status"
                                        onChange={(e) => setNewCredit({ ...newCredit, status: e.target.value })}
                                    >
                                        <MenuItem value="excellent">Excellent</MenuItem>
                                        <MenuItem value="good">Good</MenuItem>
                                        <MenuItem value="fair">Fair</MenuItem>
                                        <MenuItem value="poor">Poor</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Age (months)"
                                    type="number"
                                    value={newCredit.age_months}
                                    onChange={(e) => setNewCredit({ ...newCredit, age_months: parseInt(e.target.value) || 0 })}
                                    InputProps={{ inputProps: { min: 0 } }}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenCreditDialog(false)}>Cancel</Button>
                                <Button
                                    onClick={() => {
                                        // Add new credit history item to the list
                                        const creditHistory = [...values.financialData.creditHistory, newCredit];
                                        setFieldValue('financialData.creditHistory', creditHistory);

                                        // Reset form and close dialog
                                        setNewCredit({ type: 'credit_card', status: 'good', age_months: 12 });
                                        setOpenCreditDialog(false);
                                    }}
                                >
                                    Add
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Grid>
                </Grid>
            );

        case 'cybersecurity':
            return (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6">Cybersecurity Risk Factors</Typography>
                        <Divider sx={{ my: 1 }} />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            id="securityData.systemDetails"
                            name="securityData.systemDetails"
                            label="System Details"
                            multiline
                            rows={3}
                            value={values.securityData.systemDetails}
                            onChange={handleChange}
                            helperText="Describe your IT systems, infrastructure, and technology stack"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            id="securityData.existingControls"
                            name="securityData.existingControls"
                            label="Existing Security Controls"
                            multiline
                            rows={3}
                            value={values.securityData.existingControls}
                            onChange={handleChange}
                            helperText="List current security measures and protocols"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Known Vulnerabilities
                        </Typography>

                        <Box sx={{ mb: 2 }}>
                            {values.securityData.vulnerabilities.map((item, index) => (
                                <Paper
                                    key={index}
                                    variant="outlined"
                                    sx={{ p: 2, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                >
                                    <Box>
                                        <Typography variant="body2">
                                            <strong>Severity:</strong> {item.severity}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Count:</strong> {item.count}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        color="error"
                                        onClick={() => removeListItem('securityData.vulnerabilities', index)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Paper>
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => setOpenVulnDialog(true)}
                            >
                                Add Vulnerability
                            </Button>
                        </Box>

                        {/* Dialog for adding new vulnerability */}
                        <Dialog open={openVulnDialog} onClose={() => setOpenVulnDialog(false)}>
                            <DialogTitle>Add Vulnerability</DialogTitle>
                            <DialogContent>
                                <FormControl fullWidth sx={{ mt: 2 }}>
                                    <InputLabel>Severity</InputLabel>
                                    <Select
                                        value={newVuln.severity}
                                        label="Severity"
                                        onChange={(e) => setNewVuln({ ...newVuln, severity: e.target.value })}
                                    >
                                        <MenuItem value="low">Low</MenuItem>
                                        <MenuItem value="medium">Medium</MenuItem>
                                        <MenuItem value="high">High</MenuItem>
                                        <MenuItem value="critical">Critical</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Count"
                                    type="number"
                                    value={newVuln.count}
                                    onChange={(e) => setNewVuln({ ...newVuln, count: parseInt(e.target.value) || 1 })}
                                    InputProps={{ inputProps: { min: 1 } }}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenVulnDialog(false)}>Cancel</Button>
                                <Button
                                    onClick={() => {
                                        // Add new vulnerability to the list
                                        const vulnerabilities = [...values.securityData.vulnerabilities, newVuln];
                                        setFieldValue('securityData.vulnerabilities', vulnerabilities);

                                        // Reset form and close dialog
                                        setNewVuln({ severity: 'low', count: 1 });
                                        setOpenVulnDialog(false);
                                    }}
                                >
                                    Add
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Grid>
                </Grid>
            );

        case 'health':
            return (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6">Health & Safety Risk Factors</Typography>
                        <Divider sx={{ my: 1 }} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel id="facility-type-label">Facility Type</InputLabel>
                            <Select
                                labelId="facility-type-label"
                                id="healthData.facilityType"
                                name="healthData.facilityType"
                                value={values.healthData.facilityType}
                                label="Facility Type"
                                onChange={handleChange}
                            >
                                <MenuItem value="office">Office Building</MenuItem>
                                <MenuItem value="manufacturing">Manufacturing Facility</MenuItem>
                                <MenuItem value="healthcare">Healthcare Facility</MenuItem>
                                <MenuItem value="education">Educational Institution</MenuItem>
                                <MenuItem value="retail">Retail Space</MenuItem>
                                <MenuItem value="warehouse">Warehouse/Distribution</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            id="healthData.occupancy"
                            name="healthData.occupancy"
                            label="Occupancy (people)"
                            type="number"
                            value={values.healthData.occupancy}
                            onChange={handleChange}
                            InputProps={{ inputProps: { min: 1 } }}
                            helperText="Maximum number of people typically present"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Potential Hazards
                        </Typography>

                        <Box sx={{ mb: 2 }}>
                            {values.healthData.hazards.map((hazard, index) => (
                                <Chip
                                    key={index}
                                    label={hazard}
                                    onDelete={() => removeListItem('healthData.hazards', index)}
                                    sx={{ m: 0.5 }}
                                />
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => setOpenHazardDialog(true)}
                            >
                                Add Potential Hazard
                            </Button>
                        </Box>

                        {/* Dialog for adding hazard */}
                        <Dialog open={openHazardDialog} onClose={() => setOpenHazardDialog(false)}>
                            <DialogTitle>Add Potential Hazard</DialogTitle>
                            <DialogContent>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    margin="normal"
                                    label="Hazard Description"
                                    value={newHazard}
                                    onChange={(e) => setNewHazard(e.target.value)}
                                    placeholder="e.g., Chemical exposure, Fire risk, etc."
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenHazardDialog(false)}>Cancel</Button>
                                <Button
                                    onClick={() => {
                                        // Only add if there's text
                                        if (newHazard.trim()) {
                                            const hazards = [...values.healthData.hazards, newHazard.trim()];
                                            setFieldValue('healthData.hazards', hazards);

                                            // Reset form and close dialog
                                            setNewHazard('');
                                            setOpenHazardDialog(false);
                                        }
                                    }}
                                    disabled={!newHazard.trim()}
                                >
                                    Add
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Grid>
                </Grid>
            );

        case 'operational':
            return (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6">Operational Risk Factors</Typography>
                        <Divider sx={{ my: 1 }} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            id="operationalData.processType"
                            name="operationalData.processType"
                            label="Process Type"
                            value={values.operationalData.processType}
                            onChange={handleChange}
                            helperText="Type of operational process being assessed"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel id="resource-label">Resource Availability</InputLabel>
                            <Select
                                labelId="resource-label"
                                id="operationalData.resourceAvailability"
                                name="operationalData.resourceAvailability"
                                value={values.operationalData.resourceAvailability}
                                label="Resource Availability"
                                onChange={handleChange}
                            >
                                <MenuItem value="abundant">Abundant</MenuItem>
                                <MenuItem value="sufficient">Sufficient</MenuItem>
                                <MenuItem value="limited">Limited</MenuItem>
                                <MenuItem value="critical">Critically Low</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Process Dependencies
                        </Typography>

                        <Box sx={{ mb: 2 }}>
                            {values.operationalData.dependencies.map((dependency, index) => (
                                <Chip
                                    key={index}
                                    label={dependency}
                                    onDelete={() => removeListItem('operationalData.dependencies', index)}
                                    sx={{ m: 0.5 }}
                                />
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => setOpenDependencyDialog(true)}
                            >
                                Add Dependency
                            </Button>
                        </Box>

                        {/* Dialog for adding dependency */}
                        <Dialog open={openDependencyDialog} onClose={() => setOpenDependencyDialog(false)}>
                            <DialogTitle>Add Process Dependency</DialogTitle>
                            <DialogContent>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    margin="normal"
                                    label="Dependency Name"
                                    value={newDependency}
                                    onChange={(e) => setNewDependency(e.target.value)}
                                    placeholder="e.g., Supply chain, External vendor, etc."
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenDependencyDialog(false)}>Cancel</Button>
                                <Button
                                    onClick={() => {
                                        // Only add if there's text
                                        if (newDependency.trim()) {
                                            const dependencies = [...values.operationalData.dependencies, newDependency.trim()];
                                            setFieldValue('operationalData.dependencies', dependencies);

                                            // Reset form and close dialog
                                            setNewDependency('');
                                            setOpenDependencyDialog(false);
                                        }
                                    }}
                                    disabled={!newDependency.trim()}
                                >
                                    Add
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Grid>
                </Grid>
            );

        default:
            return (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body1" color="error">
                        Unknown domain: {domain}
                    </Typography>
                </Box>
            );
    }
};

export default RiskFactorInput;